import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { CreateDisclosureService, DSTTaxYear } from '../../statement-tracker/create-disclosure/create-new/create-disclosure.service';
import { Alert } from '../../statement-tracker/statement-tracker.service';
import { DiagnosticsService } from '../diagnostics.service';
import { Subscription, take, zip } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { DSTStatement } from '../../shared/models/dst-statement.model';
import { TrackerTabsService } from '../../statement-tracker/tracker-tabs.service';
import { DSTDynamicComponentResolverService } from '../../statement-tracker/create-disclosure/shared/dst-dynamic-component-resolver.service';
import { ModalComponent } from '@appkit4/angular-components/modal';
import { DSTSharedService } from '../../shared/dst-shared.service';
import { DSTChildFormState } from '../../shared/models/dst-child-form-state.model';
import { StmtIdBtnService } from '../../shared/components/stmt-id-btn/stmt-id-btn.service';
import { DiscModalViewerComponent } from '../../shared/components/disc-modal-viewer/disc-modal-viewer.component';
import { SaveActionResponse } from '../../../shared/_models/save-action-response.model';
import { JSONExportHeader, JSONExportService } from '../../../shared/_services/JSONExport.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { generalConfigFromGlobal, hydratedGlobalParams } from '../../../shared/workspace-element-context';
import { PtableColConfigModel } from '../../shared/models/ptable-col-config.model';
import { DstMatTableDataSource } from '../../shared/mat-table/dst-mat-table-data-source';
import { DstMatTableFilterApi } from '../../shared/mat-table/dst-mat-table-filter-api';
import { refreshDstMatStickyColumns } from '../../shared/mat-table/dst-mat-grid.util';

import {
  buildDstTaxYearItems,
  resolveDstTaxYearFromEvent,
  setDstTaxYearAriaLabel,
  syncDstTaxYearItem,
} from '../../shared/utils/dst-tax-year-appkit.support';
import { getAppkitDropdownSelection } from '../../../shared/utils/appkit-dropdown.util';

@Component({
  selector: 'gtw-transfers-review-report',
  templateUrl: './transfers-review-report.component.html',
  styleUrls: [
    '../../shared/styles/dst-shared.scss',
    '../../shared/mat-table/dst-mat-table.scss',
    './transfers-review-report.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TransfersReviewReportComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(DiscModalViewerComponent) modalViewerComponent!: DiscModalViewerComponent;
  @ViewChild(MatTable) matTable!: MatTable<any>;
  @ViewChild('matSort') matSort!: MatSort;
  @ViewChild('matGridContainer') matGridContainer!: ElementRef<HTMLElement>;
  @ViewChild('yesExplanationModal') yesExplanationModal!: ModalComponent;

  dataSource = new DstMatTableDataSource<any>([]);
  table!: DstMatTableFilterApi<any>;
  displayedColumns: string[] = [];
  filterColumns: string[] = [];

  data: any[] = [];
  cols: PtableColConfigModel[] = [
    {
      field: 'STATEMENTNAME',
      header: 'Statement',
      width: '290px',
    },
    {
      field: 'PRIMARYBUSINESS',
      header: 'Business',
      width: '250px',
    },
    {
      field: 'PRIMARYLEID',
      header: 'LEID',
      width: '110px',
    },
    {
      field: 'PRIMARYENTITY',
      header: 'Entity Name',
      width: '280px',
    },
    {
      field: 'TRANS_TYPE',
      header: 'Transaction Type',
      width: '172px',
    },
    {
      field: 'LIQUIDATIONDATE',
      header: 'Date of Transfer',
      width: '115px',
    },
    {
      field: 'CURRENCYNAME',
      header: 'Currency',
      width: '115px',
    },
    {
      field: 'BASISDESC',
      header: 'Descr. of Property Transferred',
      width: '230px',
    },
    {
      field: 'BASISNAME',
      header: 'Basis for Property Transferred',
      width: '146px',
    },
    {
      field: 'BASISAMT',
      header: 'Basis Amount',
      width: '150px',
    },
    {
      field: 'LIABILITIES',
      header: 'Liabilities Assumed',
      width: '120px',
    },
    {
      field: 'NETAMOUNT',
      header: 'Net Amount',
      width: '150px',
    },
    {
      field: 'FMVAMT',
      header: 'FMV Amount',
      width: '150px',
    },
    {
      field: 'BASISFMVALERT',
      header: 'FMV = Basis Alert',
      width: '106px',
    },
    {
      field: 'IMPORTATIONPROPERTYDESC',
      header: 'Importation Property Description',
      width: '220px',
    },
    {
      field: 'LOSSDUPLICATIONPROPERTYDESC',
      header: 'Loss Duplication Property Description',
      width: '220px',
    },
    {
      field: 'GAINLOSSPROPERTYDESC',
      header: 'Recognized Gain/Loss Property Description',
      width: '220px',
    },
    {
      field: 'POTENTIAL_BIL_IMPORTATION',
      header: 'Potential BIL Loss Importation',
      width: '145px',
    },
    {
      field: 'POTENTIAL_362_E_ALERT',
      header: 'Potential 362(e) Alert',
      width: '135px',
    },
    {
      field: 'IS_362_LIMIT_MADE',
      header: 'Is a Sec. 362(e)(2)(C) Election to Apply Sec. 362 Limitation to Transferor\'s Stock Basis being made?',
      width: '293px',
    },
    {
      field: 'REVIEW_EXPL',
      header: 'Reviewer Explanation',
      width: '240px',
    },
    {
      field: 'ISREVIEWED',
      header: 'Mark Statement as Reviewed',
      width: '140px',
    },
    {
      field: 'ISQAR',
      header: 'QAR?',
      width: '100px',
    },
  ];
  frozenCols: PtableColConfigModel[] = [
    {
      field: 'TAXYEAR',
      header: 'Year',
      width: '81px',
    },
    {
      field: 'STATEMENTID',
      header: 'Stmt ID',
      width: '117px',
    },
  ];
  loading: boolean = true;
  taxyearLoaded: boolean = false;
  excelExportFileName: string;
  taxYears: DSTTaxYear[] = [];
  taxYearItems: { value: number; label: string }[] = [];
  selectedTaxYearItem: any = {};
  taxYear: DSTTaxYear | undefined;

  activeRow!: DSTStatement;
  showModalViewer: boolean = false;
  private onDisCancelSubs!: Subscription;
  alert: Alert;
  fStatementId: string = '';
  fStatementName: string = '';
  fBusiness: string = '';
  fLeid: string = '';
  fEntityName: string = '';
  fTransactionType: string = '';
  fDateofTransfer: string = '';
  fCurrency: string = '';
  fDescPropTransfered: string = '';
  fBasisPropTransferred: string = '';
  fBasisAmount: string = '';
  fLiabilitiesAssumed: string = '';
  fNetAmount: string = '';
  fFmvAmount: string = '';
  fFmvBasisAlert: string = '';
  fImpPropDesc: string = '';
  fLossDupliPropDesc: string = '';
  fRecogGlPropDesc: string = '';
  fPotentialBilLossImport: string = '';
  fPotential362EAlert: string = '';
  fIsQar: string = '';

  revExplanations: any[] = [
    { label: 'Section Option', value: '', reviewTypeId: 0 },
  ];
  yesExplanationText: string = '';
  yesExplActiveRow: any;
  showYesExplanationModal: boolean = false;

  private contextApplied = false;

  constructor(
    private cDSerivce: CreateDisclosureService,
    public dstSharedService: DSTSharedService,
    private stmtIdBtnService: StmtIdBtnService,
    private trackerTabsService: TrackerTabsService,
    private diagnosticsService: DiagnosticsService,
    private jsonExportService: JSONExportService,
    private dCRService: DSTDynamicComponentResolverService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.excelExportFileName = 'Transfers_Review_Diagnostics_' + this.dstSharedService.getExcelExportDateString();
    this.alert = dstSharedService.alert;

    this.displayedColumns = [
      ...this.frozenCols.map((c) => c.field),
      ...this.cols.map((c) => c.field),
    ];
    this.filterColumns = this.displayedColumns.map((c) => `${c}_filter`);
    this.table = new DstMatTableFilterApi(this.dataSource);
  }

  ngOnInit(): void {
    this.onDisCancelSubs = new Subscription();
    this.onDisCancelSubs.add(
      this.dCRService.onDisclosureCancel.subscribe(() => {
        this.trackerTabsService.destroyModalTab();
        this.showModalViewer = false;
      }),
    );
    this.applyWorkspaceContext();
    this.onDisCancelSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      }),
    );
  }

  ngAfterViewInit(): void {
    this.connectMatSort();
    refreshDstMatStickyColumns(this.matTable);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    refreshDstMatStickyColumns(this.matTable);
  }

  private applyWorkspaceContext(): void {
    if (this.contextApplied) {
      return;
    }
    try {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
      const user = this.gtwApp.USER_SETTINGS?.user || {};
      const clientKey = String(user.client?.client_key || user.client_key || '');
      const ssoId = String(user.sso_id || '');
      const roleLevel = Number(this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level || 0);
      const taxYear = String(gp.tax_year || '');
      const scenario = String(gp.scenario || '');

      if (!clientKey && !ssoId) {
        return;
      }

      this.contextApplied = true;
      this.dstSharedService.setClientKey(clientKey);
      this.dstSharedService.setDSTUser(ssoId, roleLevel);
      this.dstSharedService.setSystemLockUnlockStatus(false, roleLevel);
      this.dstSharedService.setTaxYearScenario(taxYear, scenario);
      this.dstSharedService.setBaseURLs(gc);
      const getTaxYears$ = this.cDSerivce.getTaxYears();
      zip(getTaxYears$)
        .pipe(take(1))
        .subscribe((data: any) => {
          this.taxYears = data[0];
          this.setTaxYear(taxYear);
          this.loadReport();
          this.loadDiagReviewOptions();
        });
    } catch (e) {
      if (e instanceof SyntaxError) {
        console.error('JSON parse Syntax Error' + e);
      }
      this.dstSharedService.showAlert(
        'error',
        'Missing gtw input params, please contact system administrator for assistance!',
      );
    }
  }

  ngOnDestroy(): void {
    this.onDisCancelSubs?.unsubscribe();
  }

  onTaxYearChange(): void {
    this.taxyearLoaded = true;
    this.loadReport();
  }

  setTaxYear(taxYear: string): void {
    this.taxYear = this.taxYears.find((item: any) => item.text.toString() === taxYear);
    this.taxYearItems = buildDstTaxYearItems(this.taxYears);
    this.selectedTaxYearItem = syncDstTaxYearItem(this.taxYearItems, this.taxYear, taxYear);
    this.taxyearLoaded = true;
  }

  onTaxYearChanged(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTaxYearItem = selected || this.selectedTaxYearItem;
    const matched = resolveDstTaxYearFromEvent(event, this.taxYears);
    if (matched) {
      this.taxYear = matched;
      this.onTaxYearChange();
    }
  }

  onTaxYearSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  setTaxYearAriaLabel(item: any): string {
    return setDstTaxYearAriaLabel(item);
  }

  loadReport(): void {
    this.data = [];
    this.loading = true;
    this.diagnosticsService
      .loadTransfersReviewReport(this.taxYear?.text.toString()!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data) => {
          this.data = data;
          this.data.forEach((element, index) => {
            element.statementId = element.STATEMENTID;
            element.taxYearId = element.TAXYEARID;
            element.formId = element.FORMID;
            element.formName = element.FORM_NAME;
            element.FORMTYPEID = element.FORM_TYPE_ID;

            element.id = index + 1;
            element['revExplanations'] = [];
            element['selectedExplanationItem'] = null;
            this.revExplanations.forEach((item: any) => {
              const isSelected = element.TRANSFER_REIVEW_NAME === item.value;
              const explanationItem = {
                label: item.label,
                value: item.value,
                reviewTypeId: item.reviewTypeId,
                selected: isSelected,
              };
              element['revExplanations'].push(explanationItem);
              if (isSelected) {
                element['selectedExplanationItem'] = explanationItem;
              }
            });
          });
          this.resetColFilters();
          this.dataSource.data = this.data;
          this.connectMatSort();
          refreshDstMatStickyColumns(this.matTable);
        },
        (error: HttpErrorResponse) => {
          console.error(error);
          this.data = [];
          this.dataSource.data = [];
          this.dstSharedService.showAlert(
            'error',
            'Something went wrong while loading the Data Collect grid data, please try again!',
          );
        },
      )
      .add(() => {
        this.loading = false;
      });
  }

  loadDiagReviewOptions(): void {
    this.diagnosticsService
      .getDiagnosticsReviewOptions('transferReview')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data) => {
          data.forEach((item: any) => {
            this.revExplanations.push({
              label: item.REVIEW_NAME,
              value: item.REVIEW_NAME,
              reviewTypeId: item.REVIEW_TYPE_ID,
            });
          });
        },
        (error: HttpErrorResponse) => {
          console.error(error);
          this.dstSharedService.showAlert(
            'error',
            'Something went wrong while loading Review options, please try again!',
          );
        },
      );
  }

  updateYesExplReviewComment(
    statementId: string,
    reviewType: string,
    reviewTypeId: string,
    otherExpl: string,
  ): void {
    this.diagnosticsService
      .dstUpdateReviewComment(statementId, reviewType, reviewTypeId, otherExpl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: SaveActionResponse) => {
          if (response.callSuccess == 1) {
            this.dstSharedService.showAlert('success', 'Review Type Saved Successfully.');
          } else {
            this.dstSharedService.showAlert(
              'error',
              'Something went wrong while saving review type, please try again later!',
            );
          }
        },
        (error: HttpErrorResponse) => {
          console.log(error);
          this.dstSharedService.showAlert('error', error.message);
        },
      );
  }

  reviewerExplanationChange(event: any, rowData: any): void {
    const selected = Array.isArray(event?.selected)
      ? event.selected
      : event?.selected
        ? [event.selected]
        : [];
    const change = selected[0] ?? null;
    rowData.REVIEWNAME = change?.value;
    rowData.REVIEWTYPEID = change?.reviewTypeId;
    rowData.selectedExplanationItem = change;

    if (rowData.REVIEWNAME === 'Yes (provide Stmt ID in the textbox)') {
      this.yesExplanation(rowData);
    } else {
      rowData.TRANSFER_REVIEW_EXPLANATION = '';
    }

    this.updateYesExplReviewComment(
      rowData.STATEMENTID,
      'transferReview',
      change?.reviewTypeId,
      rowData.TRANSFER_REVIEW_EXPLANATION || '',
    );
  }

  yesExplanation(rowData: any): void {
    this.yesExplActiveRow = rowData;
    this.yesExplanationText = rowData.TRANSFER_REVIEW_EXPLANATION || '';
    this.showYesExplanationModal = true;
    setTimeout(() => {
      this.yesExplanationModal.showModal('rev-yes-exp');
    });
  }

  onYesExplanationSave(id: string): void {
    this.yesExplActiveRow.TRANSFER_REVIEW_EXPLANATION = this.yesExplanationText || '';

    this.updateYesExplReviewComment(
      this.yesExplActiveRow.STATEMENTID,
      'transferReview',
      this.yesExplActiveRow.REVIEWTYPEID,
      this.yesExplanationText || '',
    );

    this.yesExplanationModal.closeModalOk(id);
    setTimeout(() => {
      this.showYesExplanationModal = false;
    });
  }

  onYesExplanationCancel(id: string): void {
    this.yesExplanationModal.closeModalOk(id);
    setTimeout(() => {
      this.showYesExplanationModal = false;
    });
  }

  showViewUpdateStatement(rowData: any): void {
    this.activeRow = rowData;
    this.dstSharedService.setTableActionStatus(true);
    this.dstSharedService.setIsFiled(rowData.IS_FILED!);
    this.stmtIdBtnService
      .getChildStatements(rowData.STATEMENTID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (childFormStates: DSTChildFormState[]) => {
          this.showModalViewer = true;
          setTimeout(() => {
            this.modalViewerComponent.showMore('dst-disclosure-viewer', childFormStates);
          });
        },
        (eMsg: string) => {
          console.log(eMsg);
          this.dstSharedService.showAlert('error', eMsg);
        },
      )
      .add(() => {
        this.dstSharedService.setTableActionStatus(false);
      });
  }

  afterModalClosed(): void {
    this.trackerTabsService.destroyModalTab();
    this.showModalViewer = false;
  }

  filterCol(event: Event, field: string, matchMode: string): void {
    this.table.filter((event.target as HTMLInputElement).value, field, matchMode);
  }

  private resetColFilters(): void {
    this.fStatementId = '';
    this.fStatementName = '';
    this.fBusiness = '';
    this.fLeid = '';
    this.fEntityName = '';
    this.fTransactionType = '';
    this.fDateofTransfer = '';
    this.fCurrency = '';
    this.fDescPropTransfered = '';
    this.fBasisPropTransferred = '';
    this.fBasisAmount = '';
    this.fLiabilitiesAssumed = '';
    this.fNetAmount = '';
    this.fFmvAmount = '';
    this.fFmvBasisAlert = '';
    this.fImpPropDesc = '';
    this.fLossDupliPropDesc = '';
    this.fRecogGlPropDesc = '';
    this.fPotentialBilLossImport = '';
    this.fPotential362EAlert = '';
    this.fIsQar = '';
    this.table?.reset();
  }

  statementReviewedChange(event: any, rowData: any): void {
    rowData.markStatementAsReviewed = (event?.checkboxState ?? event?.srcElement?.checked ?? false) ? 1 : 0;
    this.stmtIdBtnService
      .setReviewed(rowData.statementId, rowData.markStatementAsReviewed)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: SaveActionResponse) => {
          if (response.callSuccess == 1) {
            this.dstSharedService.showAlert('success', 'Statement saved successfully for reviewed status.');
          } else {
            this.dstSharedService.showAlert(
              'error',
              'Something went wrong while reviewing status, please try again later!',
            );
          }
        },
        (error: HttpErrorResponse) => {
          console.log(error);
          this.dstSharedService.showAlert('error', error.message);
        },
      );
  }

  exportToExcel(): void {
    const headersMap: JSONExportHeader[] = [
      { field: 'TAXYEAR', header: 'Year' },
      { field: 'STATEMENTID', header: 'Stmt ID' },
      { field: 'STATEMENTNAME', header: 'Statement' },
      { field: 'PRIMARYBUSINESS', header: 'Business' },
      { field: 'PRIMARYLEID', header: 'LEID' },
      { field: 'PRIMARYENTITY', header: 'Entity Name' },
      { field: 'TRANS_TYPE', header: 'Transaction Type' },
      { field: 'LIQUIDATIONDATE', header: 'Date of Transfer' },
      { field: 'CURRENCYNAME', header: 'Currency' },
      { field: 'BASISDESC', header: 'Descr. of Property Transferred' },
      { field: 'BASISNAME', header: 'Basis for Property Transferred' },
      { field: 'BASISAMT', header: 'Basis Amount' },
      { field: 'LIABILITIES', header: 'Liabilities Assumed' },
      { field: 'NETAMOUNT', header: 'Net Amount' },
      { field: 'FMVAMT', header: 'FMV Amount' },
      { field: 'BASISFMVALERT', header: 'FMV = Basis Alert' },
      { field: 'IMPORTATIONPROPERTYDESC', header: 'Importation Property Description' },
      { field: 'LOSSDUPLICATIONPROPERTYDESC', header: 'Loss Duplication Property Description' },
      { field: 'GAINLOSSPROPERTYDESC', header: 'Recognized Gain/Loss Property Description' },
      { field: 'POTENTIAL_BIL_IMPORTATION', header: 'Potential BIL Loss Importation' },
      { field: 'POTENTIAL_362_E_ALERT', header: 'Potential 362(e) Alert' },
      {
        field: 'REVIEWNAME',
        header:
          'Is a Sec. 362(e)(2)(C) Election to Apply Sec. 362 Limitation to Transferor\'s Stock Basis being made?',
      },
      { field: 'REVIEW_EXPL', header: 'Reviewer Explanation' },
      { field: 'ISREVIEWED', header: 'Mark Statement as Reviewed' },
      { field: 'ISQAR', header: 'QAR?' },
      { field: 'MODIFIEDBY', header: 'Modified By' },
      { field: 'MODIFIEDONFORMATTED', header: 'Modified On' },
      { field: 'CREATEDBY', header: 'Created By' },
      { field: 'CREATEDONFORMATTED', header: 'Created On' },
    ];

    const hasActiveFilters = Object.keys(this.table.filters).length > 0;
    const exportData = hasActiveFilters ? this.dataSource.filteredData : this.dataSource.data;
    this.jsonExportService.exportToExcel(this.excelExportFileName, exportData, headersMap);
  }

  setAriaLabel(item: any, contextLabel: string = 'Option'): string {
    const label = item?.label || item?.text || '-';
    return `${contextLabel} option ${label}`.trim();
  }

  onDropdownSelectItem(_event: any): void {}

  getColStyle(col: PtableColConfigModel): Record<string, string> {
    const width = col.width || '100px';
    return { width, minWidth: width, maxWidth: width };
  }

  getColWidthPx(col: PtableColConfigModel): number {
    return this.parseWidthPx(col.width);
  }

  onColumnWidthChange(col: PtableColConfigModel, widthPx: number): void {
    const width = `${widthPx}px`;
    col.width = width;
    this.changeDetectorRef.detectChanges();
    refreshDstMatStickyColumns(this.matTable);
  }

  onMatSortChange(): void {
    this.connectMatSort();
  }

  private connectMatSort(): void {
    if (this.matSort) {
      this.dataSource.sort = this.matSort;
    }
  }

  private parseWidthPx(width?: string): number {
    if (!width) {
      return 100;
    }
    const parsed = Number.parseInt(width, 10);
    return Number.isNaN(parsed) ? 100 : parsed;
  }
}
